import {createNativeStackNavigator} from '@react-navigation/native-stack';
import {homeScreens} from '../screens';
import AnimatedLoadingModal from '~components/Matching/AnimatedLoadingModal';
import useMatchingLoadingModalAnimation from '~hooks/react-hooks/useMatchingLoadingModalAnimation';
import {useEffect, useRef} from 'react';
import {RouteProp, useNavigation, useRoute} from '@react-navigation/native';
import {NavigationProps, RootStackParamsList} from '~types/navigation/navigation.type';
import {SCREENS} from '~constants';

const Stack = createNativeStackNavigator();

export const HomeStack = () => {
  const isMountedRef = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Safely get hooks with error handling
  let hookData;
  try {
    hookData = useMatchingLoadingModalAnimation();
  } catch (error) {
    console.error('❌ Error in useMatchingLoadingModalAnimation:', error);
    hookData = {
      isMatchingLoadingModalVisible: false,
      matchingModalCallback: () => {},
      noMatchesButtonIsVisible: false,
      closeMatchingLoadingModal: () => {},
    };
  }

  const {isMatchingLoadingModalVisible, matchingModalCallback, noMatchesButtonIsVisible, closeMatchingLoadingModal} = hookData;

  const route = useRoute<RouteProp<RootStackParamsList, SCREENS.HOME_STACK>>();
  const navigation = useNavigation<NavigationProps>();

  const params = route?.params;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Safety checks to prevent memory access violations
    if (!isMountedRef.current || !navigation || !params) {
      return;
    }

    // Only navigate if we have a valid eventId from a legitimate source (deep link, notification, etc.)
    // This prevents unwanted navigation on app startup
    if (!params?.eventId || typeof params.eventId !== 'number' || params.eventId <= 0) {
      return;
    }

    console.log('🔗 [HOME STACK] Navigating to event:', params.eventId);

    timeoutRef.current = setTimeout(() => {
      // Double-check component is still mounted before navigation
      if (!isMountedRef.current || !navigation) {
        return;
      }

      try {
        navigation.navigate(SCREENS.HOME_EVENT, {eventId: params.eventId!});
        // Safely set params with null check
        if (navigation.setParams) {
          navigation.setParams({eventId: undefined});
        }
      } catch (error) {
        console.error('❌ Navigation error in HomeStack:', error);
      }

      timeoutRef.current = null;
    }, 400);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [navigation, params?.eventId]);

  // Safety check before rendering
  if (!isMountedRef.current) {
    return null;
  }

  return (
    <>
      <Stack.Navigator screenOptions={{headerShown: false}}>
        {homeScreens?.map(screen => {
          // Add safety checks for each screen
          if (!screen?.name || !screen?.component) {
            console.warn('⚠️ Invalid screen configuration:', screen);
            return null;
          }

          return (
            <Stack.Screen
              name={screen.name}
              key={screen.name}
              component={screen.component}
              options={screen.options || {}}
              initialParams={params || {}}
            />
          );
        })}
      </Stack.Navigator>
      {/* Only render modal if hooks are working properly */}
      {hookData && (
        <AnimatedLoadingModal
          isVisible={isMatchingLoadingModalVisible || false}
          callback={matchingModalCallback}
          isButtonVisible={noMatchesButtonIsVisible || false}
          close={closeMatchingLoadingModal}
        />
      )}
    </>
  );
};
